import { lazy as reactLazy, Suspense, type ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Recover from stale page files after an update: reload once instead of a blank screen.
const RELOAD_KEY = "akita-chunk-reload";
if (typeof window !== "undefined") {
  window.addEventListener("load", () => setTimeout(() => sessionStorage.removeItem(RELOAD_KEY), 10000));
}
const lazy = <T extends ComponentType<any>>(load: () => Promise<{ default: T }>) =>
  reactLazy(() =>
    load().catch((err) => {
      if (!sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, "1");
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    })
  );

const Index = lazy(() => import("./pages/Index"));
const Privacy = lazy(() => import("./pages/Privacy"));
const News = lazy(() => import("./pages/News"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const Docs = lazy(() => import("./pages/Docs"));
const Migrate = lazy(() => import("./pages/Migrate"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Admin = lazy(() => import("./pages/Admin"));

const Taskboard = lazy(() => import("./pages/Taskboard"));
const TotalSupply = lazy(() => import("./pages/TotalSupply"));
const CirculatingSupply = lazy(() => import("./pages/CirculatingSupply"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsArticle />} />
            <Route path="/blog" element={<Navigate to="/news" replace />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/migrate" element={<Migrate />} />
            <Route path="/phil" element={<Navigate to="/migrate" replace />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/taskboard" element={<Taskboard />} />
            <Route path="/api/total-supply" element={<TotalSupply />} />
            <Route path="/api/circulating-supply" element={<CirculatingSupply />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
